import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuoteLimits } from "../hooks/useQuoteRegion";

/* 쿠키스토리 견적 = print 접수 화면을 그대로 띄운다 (2026-10-20 김현비).
   "동작은 print 와 전부 똑같이, 화면만 다르게" — print 가 ?source=cookistory 로 열리면
   쿠키스토리 색·쿠키커터 고정 옵션·튜토리얼 숨김으로 보이고, [견적서 받기] 때 접수 대신
   Cookie NAS 에 견적만 남긴다. print 가 바뀌면 여기도 그대로 따라간다.
   견적서가 나오면 print 가 quote-complete 를 보내고, 그걸 받아 2번(네이버 스토어 결제)을 띄운다. */
const PRINT_ORIGIN = (process.env.REACT_APP_PRINT_QUOTE_ORIGIN || "https://print.makerz.co.kr").replace(/\/$/, "");

export default function PrintQuoteFrame({ source = "cookistory", onQuote }) {
  const { i18n } = useTranslation();
  const frameRef = useRef(null);
  const [height, setHeight] = useState(1100);
  // [새 접수하기] — print 화면을 처음 상태로 다시 연다
  const [round, setRound] = useState(0);
  const lang = (i18n.language || "ko").split("-")[0];
  // 견적서의 배송비 안내 = 쿠키스토리 배송비 설정. 설정을 받아온 뒤(또는 3초 뒤 기본값으로) 한 번만 정해
  // print 화면을 연다 — 나중에 바뀌어 화면이 다시 열리면 손님이 하던 견적이 날아간다.
  const limits = useQuoteLimits();
  const [ship, setShip] = useState(null);
  useEffect(() => {
    if (ship !== null) return undefined;
    if (limits.minOrderPrice !== null) { setShip(Number(limits.shippingBase) || 0); return undefined; }
    const timer = setTimeout(() => setShip(Number(limits.shippingBase) || 0), 3000);
    return () => clearTimeout(timer);
  }, [limits, ship]);

  useEffect(() => {
    function onMessage(e) {
      if (e.origin !== PRINT_ORIGIN) return;
      if (e.source !== frameRef.current?.contentWindow) return;
      const data = e.data;
      if (!data || typeof data !== "object") return;
      if (data.type === "makerz-print-height") {
        const h = Number(data.height);
        if (h > 200 && h < 20000) setHeight(Math.ceil(h));
      }
      if (data.type === "quote-complete" && data.orderNum) {
        const items = Array.isArray(data.items) ? data.items : [];
        onQuote?.({
          orderNum: String(data.orderNum),
          totalPrice: Number(data.totalPrice) || 0,
          items: items.map((it) => ({ name: it.fileName, qty: it.quantity, subtotal: it.price })),
          receiptUrl: data.quoteUrl || "",
          startNew: () => { onQuote?.(null); setRound((n) => n + 1); },
        });
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onQuote]);

  // 사이트 언어를 바꾸면 print 화면에도 알린다
  useEffect(() => {
    frameRef.current?.contentWindow?.postMessage({ type: "set-lang", lang }, PRINT_ORIGIN);
  }, [lang]);

  if (ship === null) return <div style={{ height }} aria-busy="true" />;

  return (
    <iframe
      key={round}
      ref={frameRef}
      className="print-quote-frame"
      title="3D 프린팅 견적"
      src={`${PRINT_ORIGIN}/upload?source=${encodeURIComponent(source)}&lang=${encodeURIComponent(lang)}&ship=${ship}`}
      style={{ width: "100%", height, border: 0, display: "block" }}
      allow="clipboard-write"
    />
  );
}
